import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import Tab from '@lwt-helix/tab';
import Account from './Account';
import MLS from './MLS';
import { setStateData } from '../../../helpers/state';
import OfficeTab from './OfficeTab';
import { useCommonStore } from '../../../../store/store';
import ComparisonSets from '../../../../comparison-sets/ComparisonSets';
import { agentProductionTerms as apt, marketShareTerms as mst, navTerms } from '../../../../constants';
import { terms as nt } from '../../../../constants/notifications';
import { getOwnerIdByTokenInfo, userNameByTokenType } from '../../../../constants/auth';
import { useAuthStore } from '../../../../store/auth/store';
import { useFeatureToggles } from '../../../hooks/featureToggles';
import { Branding } from './Branding';
import PropTypes from 'prop-types';
import { brandingTerms } from '../../../../constants/branding.js';
import { Routes } from '../../../routes/routes.js';
import Notifications from './Notifications';

const Profile = ({ userAccounts, isMarketShareAllowed }) => {
    const [state, setState] = useState({});
    const history = useHistory();
    const featureToggles = useFeatureToggles();
    const defaultSortFunction = (a, b) => a.shortDescription.localeCompare(b.shortDescription);

    const { membership } = useCommonStore(({ membership }) => ({ membership }));

    const {
        tokenInfo,
        tokenType,
    } = useAuthStore(state => ({
        tokenInfo: state.tokenInfo,
        tokenType: state.tokenType,
    }));

    const ownerId = tokenType && getOwnerIdByTokenInfo[tokenType](tokenInfo);

    const toggleActiveTab = index => {
        const items = getTabItems();
        const selectedItem = items[index];
        if (selectedItem.title === nt.notifications) {
            history.push(Routes.MY_PROFILE.BASE + Routes.MY_PROFILE.NOTIFICATIONS);
            return;
        }

        let searchParams = new URLSearchParams(history.location.search);
        searchParams.set('selectedTab', index);
        history.push({
            pathname: Routes.MY_PROFILE.BASE,
            search: searchParams.toString()
        });
    };

    useEffect(() => {
        if (!tokenInfo || !tokenType) return;
        setState({
            ...state,
            profile: {
                id: ownerId,
                firstName: userAccounts ? userAccounts.user.firstName : tokenInfo.given_name,
                lastName: userAccounts ? userAccounts.user.lastName : tokenInfo.family_name,
                username: userNameByTokenType(tokenInfo)[tokenType],
                email: userAccounts ? userAccounts.user.email : tokenInfo.email
            }
        });
    }, [tokenInfo, tokenType]);

    useEffect(() => {
        if (membership?.length) {
            const mlsList = [...membership].sort(defaultSortFunction);
            setStateData('mlsList', mlsList, setState);
            // We could progressively switch from the local state to this
            useCommonStore.setState({ mlsList });
        }
    }, [membership]);

    useEffect(() => {
        const qs = queryString.parse(history.location.search);
        const selectedTab = qs?.selectedTab && parseInt(qs.selectedTab);
        selectedTab && setStateData('currentIndex', selectedTab, setState);
    }, []);

    const getActiveTab = () => {
        if (history.location.pathname === Routes.MY_PROFILE.BASE + Routes.MY_PROFILE.NOTIFICATIONS) {
            if (featureToggles.notifications.isEnabled) {
                return getTabItems().findIndex(item => item.title === nt.notifications) || 0;
            }
            history.replace({ pathname: '/' });
            return;
        }

        const qs = queryString.parse(history.location.search);
        return qs?.selectedTab ? parseInt(qs.selectedTab) : 0;
    };

    const getTabItems = () => {
        const items = [
            {
                title: apt.account,
                content: {
                    jsx: <Account profile={state.profile} />,
                    className: 'raised-border bg-white p-4'
                }
            },
            {
                title: apt.office,
                content: {
                    jsx: <OfficeTab profile={state.profile} mlsList={state.mlsList} />,
                    className: 'raised-border bg-white p-4'
                }
            },
            {
                title: apt.mls,
                content: {
                    jsx: <MLS mlsList={state.mlsList} email={tokenInfo.email}/>,
                    className: 'raised-border bg-white p-4'
                }
            },
        ];

        if (isMarketShareAllowed && featureToggles.comparisonSets.isEnabled) {
            items.push({
                title: mst.comparisonSets,
                content: {
                    jsx: <ComparisonSets />
                }
            });
        }

        items.push({
            title: brandingTerms.branding,
            content: {
                jsx: <Branding />,
                className: 'raised-border bg-white p-4'
            }
        });

        if (featureToggles.notifications.isEnabled) {
            items.push({
                title: nt.notifications,
                content: {
                    jsx: <Notifications />,
                    className: 'raised-border bg-white p-4'
                }
            });
        }

        return items;
    };

    return (
        <>
            <div style={{ margin: '15px 10px 0' }}>
                <h3 className='text-center mb-0 font-weight-bold'>{navTerms.myProfile}</h3>
            </div>
            <div style={{ margin: '5px auto', padding: '0 20px' }}
                 className='col-xl-6 col-lg-7 col-md-8 col-sm-10 col-xs-12'>
                <Tab style={{ margin: '5px auto 10px' }}
                     tabTextTransform='none'
                     contentSize={6}
                     navSize={6}
                     items={getTabItems()}
                     tabs
                     currentIndex={getActiveTab()}
                     setCurrentIndex={toggleActiveTab}
                />
            </div>
        </>
    );
};

Profile.propTypes = {
    userAccounts: PropTypes.object,
    isMarketShareAllowed: PropTypes.bool
};

export default Profile;
